import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressao } from "@/components/ImpressaoComponent";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { AdminContext } from "@/contexts/AdminContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useEpc } from "@/contexts/EpcContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useItem } from "@/contexts/ItemContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useOrdensDeImpressao } from "@/contexts/OrdensDeImpressaoContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { alert } from "@/contexts/AlertModalContext";

import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { ImpressaoItem } from "@/types/impressao-item";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import {
  brDateToISO,
  sanitizarCodigoHex,
  verificarEpcsInvalidos,
} from "@/utils/UtilFuncoes";

import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Eye,
  HelpCircle,
  Layers,
  Loader2,
  MapPin,
  Printer,
  RefreshCw,
} from "lucide-react";

function OrdensDeImpressaoVisualizar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsLoading } = useGlobalLoader();
  const { numeroDeImpressoes, contaConfig } = useContaConfig();
  const { nome_da_ordem } = useParams();
  const { seriais } = useSeriais();
  const { isLoading } = useGlobalLoader();
  const { visualizarOrdem } = useOrdensDeImpressao();
  const [listaDeItens, setListaDeItens] = useState<string[][]>([]);
  const [epcRepetidosDialog, setEpcRepetidosDialog] = useState(false);
  const [epcRepetidosEncontrados, setEpcRepetidosEncontrados] = useState<
    { epc: string; codigo: string }[]
  >([]);
  const {
    coletaCategorias,
    criarCategorias,
    coletarCategoriasEspecificas,
    atualizarDescricaoCategoria,
  } = useCategorias();
  const { coletaSeriais, adicionarMaisUmNosSeriais } = useSeriais();
  const { coletaLocalizacoes, localizacoes } = useLocalizacoes();
  const { impressaoConfig, coletarBlocosEPC } = useImpressaoConfig();
  const { gerarEpcs } = useEpc();
  const { pesquisarItensNaBase } = useItem();
  const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] = useState("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] = useState("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] = useState("");
  const { adicionarItensNaFila } = useImpressao();
  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);
  const { requireAdmin } = useContext(AdminContext)!;
  const [gerouListaImpressao, setGerouListaImpressao] = useState(false);
  const [codigosConvertidos, setCodigosConvertidos] = useState<
    Array<{ original: string; convertido: string }>
  >([]);
  const [categoriasNaOrdem, setCategoriasNaOrdem] = useState<
    CategoriaItemResponse[]
  >([]);
  const [carregandoOrdem, setCarregandoOrdem] = useState(true);

  // ---- Preferência persistida (chave consistente) ----
  useEffect(() => {
    const checked = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(checked === "true");
  }, []);

  const codigoKey = impressaoConfig?.posicao_gtin_impressao ?? "-";
  const descKey = impressaoConfig?.campo_descricao_ordem_impressao ?? "-";
  const epcKey = impressaoConfig?.campo_ordem_tag ?? "-";
  const idExternoKey = impressaoConfig?.campo_id_externo ?? "-";

  const loc1Index = impressaoConfig?.campo_ordem_loc1
    ? String(parseInt(impressaoConfig.campo_ordem_loc1) - 1)
    : "-";
  const loc2Index = impressaoConfig?.campo_ordem_loc2
    ? String(parseInt(impressaoConfig.campo_ordem_loc2) - 1)
    : "-";
  const loc3Index = impressaoConfig?.campo_ordem_loc3
    ? String(parseInt(impressaoConfig.campo_ordem_loc3) - 1)
    : "-";
  const loc4Index = impressaoConfig?.campo_ordem_loc4
    ? String(parseInt(impressaoConfig.campo_ordem_loc4) - 1)
    : "-";

  // Campos de informação complementar da ordem. O valor salvo já é 0-based
  // (a tela de preferências grava parseInt - 1), então é usado direto como
  // accessorKey, igual ao campo de descrição. O nome exibido vem do contaConfig.
  const infoComplDefs = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
    n,
    key:
      (impressaoConfig as any)?.[`campo_info_compl_${n}_ordem_impressao`] ?? "-",
    nome: (contaConfig as any)?.[`info_compl_${n}`] || `Info. Compl. ${n}`,
  }));

  const validKeys = Array.from({ length: 20 }, (_, i) => i.toString());

  const temValorNaColuna = (colIndex: string): boolean => {
    if (colIndex === "-") return false;
    return listaDeItens.some((item) => item[parseInt(colIndex)]?.trim() !== "");
  };

  const buscarLocalizacaoPorId = (
    localizacoesList: any[],
    id: string,
  ): string => {
    for (const loc of localizacoesList) {
      if (loc._id === id) return loc.descricao;
      if (loc.children && loc.children.length > 0) {
        const resultado = buscarLocalizacaoPorId(loc.children, id);
        if (resultado) return resultado;
      }
    }
    return "";
  };

  const columns: ColumnDef<string[]>[] = [
    ...(validKeys.includes(codigoKey)
      ? [
          {
            accessorKey: codigoKey,
            header: t("ordens.codeField", { n: parseInt(codigoKey) + 1 }),
            cell: ({ row }: { row: any }) => {
              const codigo: string | undefined = row.getValue(codigoKey);
              if (!codigo)
                return (
                  <span className="text-xs text-muted-foreground">
                    {t("ordens.notDefinedParen")}
                  </span>
                );
              const convertido = sanitizarCodigoHex(codigo);
              const foiConvertido = convertido !== codigo.toUpperCase();
              return (
                <div className="flex items-center gap-2">
                  <span className="text-xs">{convertido}</span>
                  {foiConvertido && (
                    <Badge
                      variant="outline"
                      className="text-[10px] text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10 gap-1"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      {codigo}
                    </Badge>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    ...(validKeys.includes(descKey)
      ? [
          {
            accessorKey: descKey,
            header: t("ordens.descField", { n: parseInt(descKey) + 1 }),
            cell: ({ row }: { row: any }) => {
              const descricao: string | undefined = row.getValue(descKey);
              return (
                <span className="text-xs">
                  {descricao || (
                    <span className="text-muted-foreground">
                      {t("ordens.notDefinedParen")}
                    </span>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    ...(validKeys.includes(epcKey)
      ? [
          {
            accessorKey: epcKey,
            header: t("ordens.epcField", { n: parseInt(epcKey) + 1 }),
            cell: ({ row }: { row: any }) => {
              const epc: string | undefined = row.getValue(epcKey);
              return (
                <span className="text-xs">
                  {epc || (
                    <span className="text-muted-foreground">
                      {t("ordens.notDefinedParen")}
                    </span>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    ...(validKeys.includes(loc1Index) &&
    loc1Index !== "-" &&
    temValorNaColuna(loc1Index)
      ? [
          {
            accessorKey: loc1Index,
            header: t("ordens.loc1Field", { n: parseInt(loc1Index) + 1 }),
            cell: ({ row }: { row: any }) => {
              const locId: string | undefined = row.getValue(loc1Index);
              const locDescricao = locId
                ? buscarLocalizacaoPorId(localizacoes, locId)
                : "";
              return (
                <span className="text-xs">
                  {locDescricao || (
                    <span className="text-muted-foreground">
                      {t("ordens.notDefinedParen")}
                    </span>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    ...(validKeys.includes(loc2Index) &&
    loc2Index !== "-" &&
    temValorNaColuna(loc2Index)
      ? [
          {
            accessorKey: loc2Index,
            header: t("ordens.loc2Field", { n: parseInt(loc2Index) + 1 }),
            cell: ({ row }: { row: any }) => {
              const locId: string | undefined = row.getValue(loc2Index);
              const locDescricao = locId
                ? buscarLocalizacaoPorId(localizacoes, locId)
                : "";
              return (
                <span className="text-xs">
                  {locDescricao || (
                    <span className="text-muted-foreground">
                      {t("ordens.notDefinedParen")}
                    </span>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    ...(validKeys.includes(loc3Index) &&
    loc3Index !== "-" &&
    temValorNaColuna(loc3Index)
      ? [
          {
            accessorKey: loc3Index,
            header: t("ordens.loc3Field", { n: parseInt(loc3Index) + 1 }),
            cell: ({ row }: { row: any }) => {
              const locId: string | undefined = row.getValue(loc3Index);
              const locDescricao = locId
                ? buscarLocalizacaoPorId(localizacoes, locId)
                : "";
              return (
                <span className="text-xs">
                  {locDescricao || (
                    <span className="text-muted-foreground">
                      {t("ordens.notDefinedParen")}
                    </span>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    ...(validKeys.includes(loc4Index) &&
    loc4Index !== "-" &&
    temValorNaColuna(loc4Index)
      ? [
          {
            accessorKey: loc4Index,
            header: t("ordens.loc4Field", { n: parseInt(loc4Index) + 1 }),
            cell: ({ row }: { row: any }) => {
              const locId: string | undefined = row.getValue(loc4Index);
              const locDescricao = locId
                ? buscarLocalizacaoPorId(localizacoes, locId)
                : "";
              return (
                <span className="text-xs">
                  {locDescricao || (
                    <span className="text-muted-foreground">
                      {t("ordens.notDefinedParen")}
                    </span>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    ...(validKeys.includes(idExternoKey)
      ? [
          {
            accessorKey: idExternoKey,
            header: t("ordens.externalCodeField", {
              n: parseInt(idExternoKey) + 1,
            }),
            cell: ({ row }: { row: any }) => {
              const idExterno: string | undefined = row.getValue(idExternoKey);
              return (
                <span className="text-xs">
                  {idExterno || (
                    <span className="text-muted-foreground">
                      {t("ordens.notDefinedParen")}
                    </span>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    ...infoComplDefs.flatMap(({ key, nome }) =>
      validKeys.includes(key) && temValorNaColuna(key)
        ? [
            {
              accessorKey: key,
              header: `${nome} (${t("ordens.field", {
                n: parseInt(key) + 1,
              })})`,
              cell: ({ row }: { row: any }) => {
                const valor: string | undefined = row.getValue(key);
                return (
                  <span className="text-xs">
                    {valor || (
                      <span className="text-muted-foreground">
                        {t("ordens.notDefinedParen")}
                      </span>
                    )}
                  </span>
                );
              },
            },
          ]
        : [],
    ),
    ...validKeys
      .filter(
        (key) =>
          key !== codigoKey &&
          key !== descKey &&
          key !== epcKey &&
          key !== idExternoKey &&
          key !== loc1Index &&
          key !== loc2Index &&
          key !== loc3Index &&
          key !== loc4Index &&
          !infoComplDefs.some((d) => d.key === key),
      )
      .map((key) => ({
        accessorKey: key,
        header: t("ordens.field", { n: parseInt(key) + 1 }),
      })),
  ];

  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    alert.success(t("ordens.syncSuccess"));
  };

  const gerarListaParaImpressao = async () => {
    if (!impressaoConfig) {
      alert.error(t("ordens.configNotFound"));
      return;
    }
    if (!localizacaoSelecionada) {
      alert.error(t("ordens.selectLocation"));
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(t("ordens.informHardwareId"));
      return;
    }
    if (
      !impressaoConfig?.posicao_gtin_impressao ||
      !impressaoConfig?.campo_descricao_ordem_impressao
    ) {
      alert.error(t("ordens.fieldsNotConfigured"));
      return;
    }
    const categoriasExistentes: CategoriaItemResponse[] = [];

    const categoriasFormatadas: NewCategoriaItem[] = [];
    for (let i = 0; i < listaDeItens.length; i++) {
      const codigo =
        listaDeItens[i][parseInt(impressaoConfig.posicao_gtin_impressao)];
      const descricao =
        listaDeItens[i][
          parseInt(impressaoConfig.campo_descricao_ordem_impressao)
        ];
      categoriasFormatadas.push({
        descricao: descricao || "",
        ean: sanitizarCodigoHex(codigo || ""),
        id_conta: impressaoConfig.id_conta,
        inf_compl_1: listaDeItens[i][0] || "",
        inf_compl_2: listaDeItens[i][1] || "",
        inf_compl_3: listaDeItens[i][2] || "",
        inf_compl_4: listaDeItens[i][3] || "",
        inf_compl_5: listaDeItens[i][4] || "",
        inf_compl_6: listaDeItens[i][5] || "",
        inf_compl_7: listaDeItens[i][6] || "",
        inf_compl_8: listaDeItens[i][7] || "",
        inf_compl_9: listaDeItens[i][8] || "",
        inf_compl_10: listaDeItens[i][9] || "",
        inf_compl_11: listaDeItens[i][10] || "",
        inf_compl_12: listaDeItens[i][11] || "",
      });
    }

    const categoriasCriadas: CategoriaItemResponse[] = [];
    for (const categoria of categoriasFormatadas) {
      const categoriaExistente = categoriasNaOrdem.find(
        (cat) => cat.ean === categoria.ean,
      );

      const categoriasExistenteAdicionada = categoriasExistentes.find(
        (cat) => cat.ean === categoria.ean,
      );
      if (categoriaExistente && !categoriasExistenteAdicionada) {
        // A descrição da ordem manda: categoria já na base com descrição
        // divergente era impressa com a descrição antiga ({desc} no ZPL vem de
        // categoria.descricao). Atualiza (e persiste) antes de gerar o EPC/ZPL.
        const descricaoDaOrdem = (categoria.descricao || "").trim();
        let categoriaAtualizada = categoriaExistente;
        if (
          descricaoDaOrdem &&
          descricaoDaOrdem !== (categoriaExistente.descricao || "").trim()
        ) {
          const persistida = await atualizarDescricaoCategoria(
            categoriaExistente.ean,
            descricaoDaOrdem,
          );
          categoriaAtualizada = persistida || {
            ...categoriaExistente,
            descricao: descricaoDaOrdem,
          };
        }
        categoriasExistentes.push({
          ...categoriaAtualizada,
          inf_compl_1: categoria.inf_compl_1,
          inf_compl_2: categoria.inf_compl_2,
          inf_compl_3: categoria.inf_compl_3,
          inf_compl_4: categoria.inf_compl_4,
          inf_compl_5: categoria.inf_compl_5,
          inf_compl_6: categoria.inf_compl_6,
          inf_compl_7: categoria.inf_compl_7,
          inf_compl_8: categoria.inf_compl_8,
          inf_compl_9: categoria.inf_compl_9,
          inf_compl_10: categoria.inf_compl_10,
          inf_compl_11: categoria.inf_compl_11,
          inf_compl_12: categoria.inf_compl_12,
        });
      }
      const categoriaCriada = categoriasCriadas.find(
        (cat) => cat.ean === categoria.ean,
      );
      if (!categoriaExistente && !categoriaCriada) {
        const novaCategoria = await criarCategorias([categoria]);
        if (novaCategoria && novaCategoria.length > 0) {
          if (novaCategoria[0]._id) {
            categoriasCriadas.push(novaCategoria[0]);
          }
        }
      }
    }

    const categoriasECategoriasCriadas: CategoriaItemResponse[] = [
      ...categoriasExistentes,
      ...categoriasCriadas,
    ];

    const serialPorEan: Record<string, number> = {};
    seriais.forEach((s) => {
      serialPorEan[s.gtin] = s.serial + 1 || 0;
    });

    const listaParaImpressao: ImpressaoItem[] = [];
    let numero_de_impressoes = numeroDeImpressoes || 0;
    for (let i = 0; i < listaDeItens.length; i++) {
      const item = listaDeItens[i];
      const codigo = item[parseInt(impressaoConfig.posicao_gtin_impressao)];
      const codigoSanitizado = sanitizarCodigoHex(codigo || "");
      let categoria = categoriasECategoriasCriadas.find(
        (cat) => cat.ean === codigoSanitizado,
      );
      if (!categoria) {
        const descricao =
          item[parseInt(impressaoConfig.campo_descricao_ordem_impressao)];
        const novaCategoria = await criarCategorias([
          {
            descricao: descricao || "",
            ean: codigoSanitizado,
            id_conta: impressaoConfig.id_conta,
            inf_compl_1: item[0] || "",
            inf_compl_2: item[1] || "",
            inf_compl_3: item[2] || "",
            inf_compl_4: item[3] || "",
            inf_compl_5: item[4] || "",
            inf_compl_6: item[5] || "",
            inf_compl_7: item[6] || "",
            inf_compl_8: item[7] || "",
            inf_compl_9: item[8] || "",
            inf_compl_10: item[9] || "",
            inf_compl_11: item[10] || "",
            inf_compl_12: item[11] || "",
          },
        ]);
        if (novaCategoria && novaCategoria.length > 0 && novaCategoria[0]._id) {
          categoria = novaCategoria[0];
          categoriasECategoriasCriadas.push(novaCategoria[0]);
        } else {
          alert.error(t("ordens.errorCreatingCategory", { codigo }));
          continue;
        }
      }

      if (!serialPorEan[codigoSanitizado]) serialPorEan[codigoSanitizado] = 0;
      const serialAtual = serialPorEan[codigoSanitizado];

      const localizacoesZpl: string[] = [
        localizacoes.find((loc) => loc._id === localizacaoSelecionada)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((loc) => loc._id === localizacaoSelecionada2)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((loc) => loc._id === localizacaoSelecionada3)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((n3) => n3._id === localizacaoSelecionada3)
          ?.children.find((loc) => loc._id === localizacaoSelecionada4)
          ?.descricao || "",
      ];

      const epcs = gerarEpcs(
        {
          ...categoria,
          inf_compl_1: item[0] || "",
          inf_compl_2: item[1] || "",
          inf_compl_3: item[2] || "",
          inf_compl_4: item[3] || "",
          inf_compl_5: item[4] || "",
          inf_compl_6: item[5] || "",
          inf_compl_7: item[6] || "",
          inf_compl_8: item[7] || "",
          inf_compl_9: item[8] || "",
          inf_compl_10: item[9] || "",
          inf_compl_11: item[10] || "",
          inf_compl_12: item[11] || "",
        },
        1,
        localizacoesZpl,
        coletarBlocosEPC(),
        serialAtual,
      );
      let epc = epcs[0];
      if (
        impressaoConfig.campo_ordem_tag &&
        !impressaoConfig.gravacao_reversa
      ) {
        epc = {
          ...epc,
          epc: `${
            listaDeItens[i][parseInt(impressaoConfig.campo_ordem_tag)] ||
            epc.epc
          }`,
        };
      }

      let id_externo = "";
      if (!impressaoConfig.gravacao_reversa) {
        if (
          impressaoConfig?.campo_id_externo &&
          parseInt(impressaoConfig.campo_id_externo)
        ) {
          id_externo = item[parseInt(impressaoConfig.campo_id_externo)];
        }
      }
      if (
        impressaoConfig?.campo_ordem_tag &&
        parseInt(impressaoConfig.campo_ordem_tag) &&
        impressaoConfig.gravacao_reversa
      ) {
        id_externo = item[parseInt(impressaoConfig.campo_ordem_tag)];
      }

      listaParaImpressao.push({
        _id: epc?._id,
        inf_compl_1: item[0] || "",
        inf_compl_2: item[1] || "",
        inf_compl_3: item[2] || "",
        inf_compl_4: item[3] || "",
        inf_compl_5: item[4] || "",
        inf_compl_6: item[5] || "",
        inf_compl_7: item[6] || "",
        inf_compl_8: item[7] || "",
        inf_compl_9: item[8] || "",
        inf_compl_10: item[9] || "",
        inf_compl_11: item[10] || "",
        inf_compl_12: item[11] || "",
        tag: epc?.epc || "",
        zpl_impressao: epc?.zpl_impressao_bo || "",
        id_categoria: categoria._id,
        codigo: categoria.ean,
        descricao: categoria.descricao,
        id_conta: impressaoConfig.id_conta,
        id_usuario: impressaoConfig.id_usuario,
        id_localizacao1:
          item[
            impressaoConfig?.campo_ordem_loc1
              ? parseInt(impressaoConfig.campo_ordem_loc1) - 1
              : -1
          ] || localizacaoSelecionada,
        id_localizacao2:
          item[
            impressaoConfig?.campo_ordem_loc2
              ? parseInt(impressaoConfig.campo_ordem_loc2) - 1
              : -1
          ] || localizacaoSelecionada2,
        id_localizacao3:
          item[
            impressaoConfig?.campo_ordem_loc3
              ? parseInt(impressaoConfig.campo_ordem_loc3) - 1
              : -1
          ] || localizacaoSelecionada3,
        id_localizacao4:
          item[
            impressaoConfig?.campo_ordem_loc4
              ? parseInt(impressaoConfig.campo_ordem_loc4) - 1
              : -1
          ] || localizacaoSelecionada4,
        valor: epc?.valor_bo || 0,
        quantidade: epc?.quantidade_bo || "",
        dt_validade: brDateToISO(epc?.dt_validade_bo) || "",
        n_impressao: numero_de_impressoes.toString(),
        id_externo,
        ultima_localizacao: {
          verificar: contaConfig?.transferencia_loc_origem || false,
          primeira_vez: true,
        },
      });

      numero_de_impressoes++;
      serialPorEan[codigoSanitizado]++;
    }
    if (listaParaImpressao.length === 0) {
      alert.error(t("ordens.noEpcGenerated"));
      return;
    }
    if (!permitirItensRepetidos) {
      const itensNaBase = await pesquisarItensNaBase(
        listaParaImpressao.map((item) => item.tag),
      );
      if (itensNaBase.length > 0) {
        setEpcRepetidosEncontrados(
          listaParaImpressao
            .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
            .map((item) => ({ epc: item.tag, codigo: item.codigo })),
        );
        setEpcRepetidosDialog(true);
        adicionarMaisUmNosSeriais(
          listaParaImpressao
            .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
            .map((item) => item.codigo),
        );
        return;
      }
    }
    const epcs_para_impressao = listaParaImpressao.map((item) => item.tag);
    if (verificarEpcsInvalidos(epcs_para_impressao)) {
      alert.error(t("ordens.invalidEpcs"));
      return;
    }
    setGerouListaImpressao(true);
    adicionarItensNaFila(
      listaParaImpressao,
      nome_da_ordem,
      undefined,
      MODOS_IMPRESSAO.PEDIDO,
      nome_da_ordem,
    );
    navigate(`/importar-ordem`);
  };

  // ---- Localização padrão ----
  useEffect(() => {
    if (impressaoConfig && localizacoes.length > 0) {
      const n1 = localizacoes.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao,
      );
      setLocalizacaoSelecionada(n1?._id || "");
      const n2 = n1?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_2,
      );
      setLocalizacaoSelecionada2(n2?._id || "");
      const n3 = n2?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_3,
      );
      setLocalizacaoSelecionada3(n3?._id || "");
      const n4 = n3?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_4,
      );
      setLocalizacaoSelecionada4(n4?._id || "");
    }
    if (gerouListaImpressao) setGerouListaImpressao(false);
  }, [impressaoConfig, localizacoes, gerouListaImpressao]);

  // ---- Coletar dados da ordem ----
  const coletarDadosDaOrdem = async () => {
    setIsLoading(true);
    setCarregandoOrdem(true);
    try {
      if (listaDeItens.length === 0 && nome_da_ordem) {
        const ordem_data = await visualizarOrdem(nome_da_ordem);
        if (ordem_data) {
          const categoriasDaOrdem = ordem_data.map((item) => {
            const codigo =
              item[parseInt(impressaoConfig?.posicao_gtin_impressao || "-")];
            return codigo || "";
          });
          const categoriasColetadas =
            await coletarCategoriasEspecificas(categoriasDaOrdem);
          setCategoriasNaOrdem(categoriasColetadas);
          setListaDeItens(ordem_data);
          const conversoes: Array<{ original: string; convertido: string }> =
            [];
          const codigoIdx = parseInt(
            impressaoConfig?.posicao_gtin_impressao || "-",
          );
          if (!isNaN(codigoIdx)) {
            for (const row of ordem_data) {
              const codigoOriginal = row[codigoIdx] || "";
              if (codigoOriginal) {
                const convertido = sanitizarCodigoHex(codigoOriginal);
                if (
                  convertido !== codigoOriginal.toUpperCase() &&
                  !conversoes.some((c) => c.original === codigoOriginal)
                ) {
                  conversoes.push({ original: codigoOriginal, convertido });
                }
              }
            }
          }
          setCodigosConvertidos(conversoes);
        }
      }
    } catch (error) {
      console.error("Erro ao coletar dados da ordem:", error);
      alert.error(t("ordens.errorCollecting"));
    } finally {
      setIsLoading(false);
      setCarregandoOrdem(false);
    }
  };

  useEffect(() => {
    if (impressaoConfig) coletarDadosDaOrdem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impressaoConfig]);

  // ---- Empty / loading states ----
  if (!nome_da_ordem) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Card>
          <CardContent className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                {t("ordens.noOrderName")}
              </p>
              <p className="text-xs">{t("ordens.noOrderNameDesc")}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/importar-ordem")}
            >
              <ArrowLeft className="h-4 w-4" />
              {t("ordens.backToList")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (carregandoOrdem) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Card>
          <CardContent className="py-20 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                {t("ordens.loadingOrder")}
              </p>
              <p className="text-xs">{nome_da_ordem}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* AlertDialog: EPCs repetidos */}
      <AlertDialog
        open={epcRepetidosDialog}
        onOpenChange={setEpcRepetidosDialog}
      >
        <AlertDialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>{t("ordens.repeatedEpcsFound")}</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {t("ordens.repeatedEpcsDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="overflow-y-auto max-h-64 rounded-md border">
            <ul className="divide-y">
              {epcRepetidosEncontrados.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-3 py-2 gap-2"
                >
                  <span className="text-xs truncate">{item.epc}</span>
                  <Badge variant="secondary">{item.codigo}</Badge>
                </li>
              ))}
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("common2.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
              <Eye className="h-4 w-4" />
              {t("common2.understood")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        {/* Header com Voltar + Nome da ordem + Toolbar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => navigate("/importar-ordem")}
              title={t("ordens.backToListTitle")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold tracking-tight truncate">
                {nome_da_ordem}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("ordens.itemsInOrder", { count: listaDeItens.length })}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <TooltipProvider delayDuration={250}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9">
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {t("ordens.helpTooltip")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
              <Switch
                id="ordem-repetidos"
                disabled={isLoading}
                checked={permitirItensRepetidos}
                onCheckedChange={(checked) => {
                  requireAdmin(() => {
                    setPermitirItensRepetidos(checked);
                    localStorage.setItem(
                      "permitirItensRepetidos",
                      String(checked),
                    );
                  });
                }}
              />
              <Label
                htmlFor="ordem-repetidos"
                className="text-xs cursor-pointer"
              >
                {t("ordens.allowRepeated")}
              </Label>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={sincronizar}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">{t("ordens.sync")}</span>
            </Button>

            <Button
              size="sm"
              onClick={gerarListaParaImpressao}
              disabled={isLoading || listaDeItens.length === 0}
              title={
                listaDeItens.length === 0
                  ? t("ordens.noItemsToPrint")
                  : !localizacaoSelecionada
                    ? t("ordens.selectALocation")
                    : t("ordens.printNLabels", { count: listaDeItens.length })
              }
            >
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">{t("ordens.print")}</span>
              {listaDeItens.length > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1 h-5 px-1.5 text-[10px] bg-white/20 text-primary-foreground hover:bg-white/20"
                >
                  {listaDeItens.length}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Aviso de conversão */}
        {codigosConvertidos.length > 0 && (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-3 flex items-start gap-2 text-xs">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1 text-amber-700 dark:text-amber-300 min-w-0">
                <span className="font-medium">
                  {t("ordens.codesConverted", {
                    count: codigosConvertidos.length,
                  })}
                </span>
                <span className="opacity-80">
                  {t("ordens.codesConvertedDesc")}
                </span>
                <ul className="mt-1 list-disc list-inside space-y-0.5 max-h-24 overflow-y-auto">
                  {codigosConvertidos.map(({ original, convertido }) => (
                    <li key={original}>
                      <span>{original}</span>
                      <span className="mx-1">→</span>
                      <span className="font-medium">{convertido}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 1. Localizações */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                1
              </div>
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                {t("ordens.locations")}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="grid gap-2">
                <Label
                  htmlFor="ordem-loc-1"
                  className="text-xs text-muted-foreground"
                >
                  {t("ordens.level1")} <span className="text-destructive">*</span>
                </Label>
                <Select
                  disabled={isLoading}
                  value={localizacaoSelecionada}
                  onValueChange={(value) => {
                    setLocalizacaoSelecionada(value);
                    setLocalizacaoSelecionada2("");
                    setLocalizacaoSelecionada3("");
                    setLocalizacaoSelecionada4("");
                  }}
                >
                  <SelectTrigger id="ordem-loc-1" className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes.map((l) => (
                      <SelectItem key={l._id} value={l._id}>
                        {l.descricao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label
                  htmlFor="ordem-loc-2"
                  className="text-xs text-muted-foreground"
                >
                  {t("ordens.level2")}
                </Label>
                <Select
                  disabled={isLoading || !localizacaoSelecionada}
                  value={localizacaoSelecionada2}
                  onValueChange={(v) => {
                    setLocalizacaoSelecionada2(v);
                    setLocalizacaoSelecionada3("");
                    setLocalizacaoSelecionada4("");
                  }}
                >
                  <SelectTrigger id="ordem-loc-2" className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes
                      .find((n1) => n1._id === localizacaoSelecionada)
                      ?.children.map((n2) => (
                        <SelectItem key={n2._id} value={n2._id}>
                          {n2.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label
                  htmlFor="ordem-loc-3"
                  className="text-xs text-muted-foreground"
                >
                  {t("ordens.level3")}
                </Label>
                <Select
                  disabled={
                    isLoading ||
                    !localizacaoSelecionada ||
                    !localizacaoSelecionada2
                  }
                  value={localizacaoSelecionada3}
                  onValueChange={(v) => {
                    setLocalizacaoSelecionada3(v);
                    setLocalizacaoSelecionada4("");
                  }}
                >
                  <SelectTrigger id="ordem-loc-3" className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes
                      .find((n1) => n1._id === localizacaoSelecionada)
                      ?.children.find(
                        (n2) => n2._id === localizacaoSelecionada2,
                      )
                      ?.children.map((n3) => (
                        <SelectItem key={n3._id} value={n3._id}>
                          {n3.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label
                  htmlFor="ordem-loc-4"
                  className="text-xs text-muted-foreground"
                >
                  {t("ordens.level4")}
                </Label>
                <Select
                  disabled={
                    isLoading ||
                    !localizacaoSelecionada ||
                    !localizacaoSelecionada2 ||
                    !localizacaoSelecionada3
                  }
                  value={localizacaoSelecionada4}
                  onValueChange={setLocalizacaoSelecionada4}
                >
                  <SelectTrigger id="ordem-loc-4" className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes
                      .find((n1) => n1._id === localizacaoSelecionada)
                      ?.children.find(
                        (n2) => n2._id === localizacaoSelecionada2,
                      )
                      ?.children.find(
                        (n3) => n3._id === localizacaoSelecionada3,
                      )
                      ?.children.map((n4) => (
                        <SelectItem key={n4._id} value={n4._id}>
                          {n4.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Itens da ordem */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  2
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  {t("ordens.orderItems")}
                </CardTitle>
              </div>
              <Badge variant="secondary" className="font-medium">
                {t("ordens.itemsCount", { count: listaDeItens.length })}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable columns={columns} data={listaDeItens} />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default OrdensDeImpressaoVisualizar;
